// ** React Imports
import { useContext } from "react";

// ** Redux & Store & Actions
import { useSelector, useDispatch } from "react-redux";
import { setUserLimit } from "store/modules/user-profile/user-profile-main.slice";

// ** Custom Components
import Text from "components/typography/Text";
import DatePresenter from "components/other/DatePresenter";
import UserMediaSkeletor from "components/user/UserMediaSkeletor";
import DynamicLockIcon from "components/icons/DynamicLockIcon";
import Block from "components/layout/Block";
import { StyledMediaTypePaddingContainerMobile } from "components/media/styles";

// ** Media Components
import MediaType from "../../media/MediaType";
import MediaTypeGallery_Ratio_4_3 from "components/media/MediaTypeGallery_Ratio_4_3";
import MediaPostInteractions from "./components/MediaPostInteractions";

// ** Utils & Helpers
import generatePath from "utils/generatePath";
import { VIP_STATUS } from "helpers/constants";
import convertText from "utils/convertText";

// ** Routes
import containers from "containers";

// ** Contexts
import authContext from "contexts/authContext";
import deviceTypeContext from "contexts/deviceTypeContext";

// #####################################################

const MediaPost = ({
	item,
	user,
	sizePost,
	noPaddingAndMarginXInMobile = false,
	showCommentsOnInit = true,
}) => {
	const isAuth = useContext(authContext);
	const { isPhone } = useContext(deviceTypeContext);
	const dispatch = useDispatch();
	const currentLoggedUser = useSelector((state) => state.globalUser);

	const isMe = user.id === currentLoggedUser.id;

	const userLimit = useSelector(
		(state) => state.userProfileMain.tolerances.limit
	);
	const type = item.media_type !== undefined ? item.media_type : "post";
	const to = generatePath(containers.modal.path, {
		contentId: item.id,
		login: user.login?.toLowerCase(),
		type,
	});

	const statusVip = () => {
		if (isMe) {
			if (currentLoggedUser?.showStatusVip === false) {
				if (currentLoggedUser?.status_vip > VIP_STATUS.VERIFIED) {
					return VIP_STATUS.VERIFIED;
				}
				return currentLoggedUser.status_vip;
			}
			return currentLoggedUser.status_vip;
		}
		return user?.status_vip;
	};

	// #####################################################

	return (
		<>
			{isPhone ? (
				<Block flex column withRowGap mb={3}>
					<Block mx={noPaddingAndMarginXInMobile ? 0 : 4}>
						<UserMediaSkeletor
							noMarginBottom
							alignCenter
							user={{ ...user, status_vip: statusVip() }}
							size="small"
							color={"primary"}
							content={
								<Text small color={"grey"}>
									<DatePresenter
										date={item.date_add}
										onlyDate
									/>
									{item.private ? (
										<DynamicLockIcon
											customMargin="0 0 0 0.5rem"
											customFill="whiteAlways"
										/>
									) : null}
								</Text>
							}
							vipProps={{
								height: 14,
								marginBottom: 0.125,
							}}
						/>
					</Block>
					{(item.media_type === "photo" ||
						item.media_type === "video") && (
						<MediaTypeGallery_Ratio_4_3
							type={type}
							item={item}
							isAuth={isAuth}
							isPhone={isPhone}
							size={sizePost}
							to={to}
						/>
					)}
					{(item.description || item.text) && (
						<Block flex column>
							<StyledMediaTypePaddingContainerMobile
								$noPaddingAndMarginXInMobile={
									noPaddingAndMarginXInMobile
								}
							>
								{convertText({
									pieces: item.description || item.text,
								})}
							</StyledMediaTypePaddingContainerMobile>
						</Block>
					)}
				</Block>
			) : (
				<MediaType
					type={type}
					item={item}
					isAuth={isAuth}
					isPhone={isPhone}
					to={to}
					isProfile={true}
					size={sizePost}
					userLogin={
						<UserMediaSkeletor
							alignCenter
							user={{ ...user, status_vip: statusVip() }}
							size="small"
							content={
								<Text small color="grey">
									<DatePresenter
										date={item.date_add}
										onlyDate
									/>
									{item.private ? (
										<DynamicLockIcon customMargin="0 0 0 0.5rem" />
									) : null}
								</Text>
							}
							vipProps={{ height: 14, marginBottom: 0.125 }}
						/>
					}
				/>
			)}
			<Block
				mt={!isPhone ? 3 : 0}
				mx={noPaddingAndMarginXInMobile ? 0 : isPhone ? 4 : 0}
			>
				<MediaPostInteractions
					item={item}
					user={user}
					userLimit={userLimit}
					setUserLimit={(value) => {
						dispatch(setUserLimit(value));
					}}
					showCommentsOnInit={showCommentsOnInit}
				/>
			</Block>
		</>
	);
};

// #####################################################

export default MediaPost;
